import React, {
  useState, useMemo, useCallback, useEffect, useRef, useReducer
} from 'react';
import {
  Search, X, Plus, Minus, Trash2, CreditCard, Banknote,
  ShoppingCart, Receipt, Loader2, Smartphone, Bike, Package,
  UtensilsCrossed, CheckCircle2, Tag, Clock, Printer,
  SplitSquareHorizontal, MapPin, Phone, Hash, MessageSquare,
  Wifi, WifiOff, ChefHat, RefreshCw, Table2, Edit3, XCircle,
  ListOrdered, Timer, CheckSquare, ChevronDown, User,
  AlertTriangle, Send, CheckCheck, Ban, FileText
} from 'lucide-react';
import Navbar from '../../components/Navbar';
import axios from 'axios';
import { baseurl, imgurl } from '../../baseurl';
import '../../assets/css/order.css';

/* ═══════════════════════════════════════════════════
   CONSTANTS
═══════════════════════════════════════════════════ */
const ORDER_TYPES = [
  { id: 'table',  label: 'Dine In', icon: <UtensilsCrossed size={13}/>, color: '#ff8c00', comingSoon: false },
  { id: 'parcel', label: 'Parcel',  icon: <Package size={13}/>,          color: '#8b5cf6', comingSoon: false },
  { id: 'zomato', label: 'Zomato',  icon: <Bike size={13}/>,             color: '#e23744', comingSoon: true  },
  { id: 'swiggy', label: 'Swiggy',  icon: <Bike size={13}/>,             color: '#fc8019', comingSoon: true  },
  { id: 'other',  label: 'Other',   icon: <Smartphone size={13}/>,       color: '#22c55e', comingSoon: false },
];

const PAYMENT_METHODS = [
  { id: 'cash',  label: 'Cash',  icon: <Banknote size={13}/>              },
  { id: 'card',  label: 'Card',  icon: <CreditCard size={13}/>            },
  { id: 'upi',   label: 'UPI',   icon: <Smartphone size={13}/>            },
  { id: 'split', label: 'Split', icon: <SplitSquareHorizontal size={13}/> },
];

const ORDER_STATUS_META = {
  kot:          { label: 'KOT',       color: '#6366f1', bg: 'rgba(99,102,241,0.10)'  },
  placed:       { label: 'Placed',    color: '#ff8c00', bg: 'rgba(255,140,0,0.10)'   },
  preparing:    { label: 'Preparing', color: '#f59e0b', bg: 'rgba(245,158,11,0.10)'  },
  ready:        { label: 'Ready',     color: '#22c55e', bg: 'rgba(34,197,94,0.10)'   },
  served:       { label: 'Served',    color: '#9ca3af', bg: 'rgba(156,163,175,0.10)' },
  cancelled:    { label: 'Cancelled', color: '#ef4444', bg: 'rgba(239,68,68,0.10)'   },
  placed_print: { label: 'Printed',   color: '#0ea5e9', bg: 'rgba(14,165,233,0.10)'  },
};

const GST_RATES = [0, 5, 12, 18, 28];

/* ═══════════════════════════════════════════════════
   OFFLINE / PERSISTENCE
═══════════════════════════════════════════════════ */
const OFFLINE_KEY        = 'rw_offline_orders';
const OFFLINE_UPDATE_KEY = 'rw_offline_updates';
const SLOTS_PERSIST_KEY  = 'rw_active_slots';

const loadQ    = ()  => { try { return JSON.parse(localStorage.getItem(OFFLINE_KEY) || '[]'); } catch { return []; } };
const saveQ    = q   => localStorage.setItem(OFFLINE_KEY, JSON.stringify(q));
const addQ     = p   => { const q = loadQ(); if (p.tempId && q.find(x => x.tempId === p.tempId)) return; q.push({ ...p, queuedAt: new Date().toISOString() }); saveQ(q); };
const removeQ  = tid => saveQ(loadQ().filter(q => q.tempId !== tid));

const loadUQ   = ()  => { try { return JSON.parse(localStorage.getItem(OFFLINE_UPDATE_KEY) || '[]'); } catch { return []; } };
const saveUQ   = q   => localStorage.setItem(OFFLINE_UPDATE_KEY, JSON.stringify(q));
const addUQ    = p   => { const q = loadUQ(); if (p.updateTempId && q.find(x => x.updateTempId === p.updateTempId)) return; q.push({ ...p, queuedAt: new Date().toISOString() }); saveUQ(q); };
const removeUQ = tid => saveUQ(loadUQ().filter(q => q.updateTempId !== tid));

const persistSlots      = s => { try { localStorage.setItem(SLOTS_PERSIST_KEY, JSON.stringify({ order: s.order, activeKey: s.activeKey, byKey: s.byKey })); } catch (_) {} };
const loadPersistedSlots= () => { try { const r = localStorage.getItem(SLOTS_PERSIST_KEY); return r ? JSON.parse(r) : null; } catch { return null; } };
const clearPersistedSlots = () => localStorage.removeItem(SLOTS_PERSIST_KEY);

const newTempId = () => `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

/* ═══════════════════════════════════════════════════
   BLANK SLOT
═══════════════════════════════════════════════════ */
const BLANK_SLOT = {
  items: [], customerName: '', customerPhone: '', orderNote: '',
  discountType: 'percent', discountVal: '', deliveryCharge: '',
  externalOrderId: '', payMethod: 'cash',
  splitCash: '', splitCard: '', splitUpi: '',
  sentToKitchen: false, kotItems: [],
  gstRate: 5, gstType: 'exclusive',
};

/* ═══════════════════════════════════════════════════
   SLOTS REDUCER
═══════════════════════════════════════════════════ */
function slotsReducer(state, action) {
  switch (action.type) {
    case 'ADD_SLOT': {
      if (state.byKey[action.key]) return { ...state, activeKey: action.key };
      return { ...state, order: [...state.order, action.key], byKey: { ...state.byKey, [action.key]: { ...BLANK_SLOT, slotKey: action.key, slotType: action.slotType, tableId: action.tableId || null, tableMeta: action.tableMeta || null } }, activeKey: action.key };
    }
    case 'REMOVE_SLOT': {
      const order = state.order.filter(k => k !== action.key);
      const byKey = { ...state.byKey }; delete byKey[action.key];
      return { ...state, order, byKey, activeKey: action.key === state.activeKey ? (order[0] || null) : state.activeKey };
    }
    case 'SET_ACTIVE': return { ...state, activeKey: action.key };
    case 'PATCH_SLOT': { const s = state.byKey[action.key]; if (!s) return state; return { ...state, byKey: { ...state.byKey, [action.key]: { ...s, ...action.patch } } }; }
    case 'ADD_ITEM': { const s = state.byKey[action.key]; if (!s) return state; const cid = `${action.item.id}_${action.item.variant}`; const ex = s.items.find(i => i.cartId === cid); return { ...state, byKey: { ...state.byKey, [action.key]: { ...s, items: ex ? s.items.map(i => i.cartId === cid ? { ...i, qty: i.qty + 1 } : i) : [...s.items, { ...action.item, cartId: cid, qty: 1, note: '' }] } } }; }
    case 'UPDATE_QTY': { const s = state.byKey[action.key]; if (!s) return state; return { ...state, byKey: { ...state.byKey, [action.key]: { ...s, items: s.items.map(i => i.cartId === action.cartId ? { ...i, qty: Math.max(0, i.qty + action.delta) } : i).filter(i => i.qty > 0) } } }; }
    case 'REMOVE_ITEM': { const s = state.byKey[action.key]; if (!s) return state; return { ...state, byKey: { ...state.byKey, [action.key]: { ...s, items: s.items.filter(i => i.cartId !== action.cartId) } } }; }
    case 'SET_ITEM_NOTE': { const s = state.byKey[action.key]; if (!s) return state; return { ...state, byKey: { ...state.byKey, [action.key]: { ...s, items: s.items.map(i => i.cartId === action.cartId ? { ...i, note: action.note } : i) } } }; }
    case 'MARK_KOT': { const s = state.byKey[action.key]; if (!s) return state; return { ...state, byKey: { ...state.byKey, [action.key]: { ...s, sentToKitchen: true, kotItems: s.items.map(i => ({ ...i })) } } }; }
    case 'LOAD_FOR_EDIT': { const key = action.key; return { ...state, order: state.order.includes(key) ? state.order : [...state.order, key], byKey: { ...state.byKey, [key]: action.slot }, activeKey: key }; }
    case 'CLEAR_SLOT': { const s = state.byKey[action.key]; if (!s) return state; return { ...state, byKey: { ...state.byKey, [action.key]: { ...BLANK_SLOT, slotKey: action.key, slotType: s.slotType, tableId: s.tableId, tableMeta: s.tableMeta } } }; }
    default: return state;
  }
}
const INIT_SLOTS = { order: [], byKey: {}, activeKey: null };

/* ═══════════════════════════════════════════════════
   AXIOS
═══════════════════════════════════════════════════ */
const api = () => {
  const token = localStorage.getItem('restaurant_token');
  return axios.create({ baseURL: baseurl, headers: { Authorization: `Bearer ${token}` } });
};

/* ═══════════════════════════════════════════════════
   BILLING CALC
═══════════════════════════════════════════════════ */
function calcBill(slot) {
  const rate    = (parseFloat(slot.gstRate) || 5) / 100;
  const gstType = slot.gstType || 'exclusive';
  const rawSub  = slot.items.reduce((s, i) => s + i.price * i.qty, 0);
  const dv      = parseFloat(slot.discountVal) || 0;
  const disc    = dv <= 0 ? 0 : slot.discountType === 'percent' ? Math.min(rawSub, rawSub * dv / 100) : Math.min(rawSub, dv);
  const after   = rawSub - disc;
  const delivery= parseFloat(slot.deliveryCharge) || 0;
  let subtotal, taxAmount, grandTotal;
  if (gstType === 'inclusive') {
    taxAmount  = after * rate / (1 + rate);
    subtotal   = after - taxAmount;
    grandTotal = after + delivery;
  } else {
    subtotal   = after;
    taxAmount  = after * rate;
    grandTotal = after + taxAmount + delivery;
  }
  return { subtotal, discountAmount: disc, taxAmount, delivery, grandTotal, gstRate: parseFloat(slot.gstRate) || 5, gstType };
}

/* ═══════════════════════════════════════════════════
   TOAST
═══════════════════════════════════════════════════ */
function useToast() {
  const [t, setT] = useState({ show: false, msg: '', type: 'success' });
  const fire = useCallback((msg, type = 'success') => { setT({ show: true, msg, type }); setTimeout(() => setT(x => ({ ...x, show: false })), 2800); }, []);
  return { toast: t, fire };
}

/* ═══════════════════════════════════════════════════
   BILL PRINT HELPER
   Opens a new window with a formatted receipt
═══════════════════════════════════════════════════ */
// function printBill(order, restaurantName = 'Restrowave Restaurant') {
function printTableBill(order, restaurant = {}) {
  // restaurant can be a string (legacy) or an object {name,tagline,phone,...}
  const prof     = typeof restaurant === 'string' ? { name: restaurant } : (restaurant || {});
  const rName    = prof.name       || 'Restaurant';
  const tagline  = prof.tagline    || '';
  const phone    = prof.phone      || '';
  const address  = [prof.address, prof.city, prof.state, prof.pincode].filter(Boolean).join(', ');
  const gstin    = prof.gst_number || '';
  const footer   = prof.footer_note || 'Thank you for dining with us!';
  const logoUrl  = prof.logo_url   || '';
 
 

  const items = (() => {
  try { return typeof order.items === 'string' ? JSON.parse(order.items || '[]') : (order.items || []); }
  catch { return []; }
})();



  const dateStr  = new Date().toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
 
  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>Bill – ${order.tableLabel || 'Table'}</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box;}
    body{font-family:'Courier New',monospace;font-size:12px;color:#111;width:80mm;margin:0 auto;padding:10px 8px;}
    .hdr{text-align:center;margin-bottom:6px;}
    .logo{max-width:72px;max-height:52px;display:block;margin:0 auto 5px;}
    .rname{font-size:17px;font-weight:900;letter-spacing:.3px;}
    .tagline{font-size:10px;color:#555;margin-top:2px;}
    .rmeta{font-size:10px;color:#555;margin-top:1px;}
    .gstin{font-size:9px;color:#555;font-weight:700;margin-top:2px;}
    .inv-lbl{text-align:center;font-size:10px;font-weight:900;letter-spacing:3px;text-transform:uppercase;margin:4px 0;}
    .div-s{border-top:1px solid #111;margin:5px 0;}
    .div-d{border-top:1px dashed #999;margin:4px 0;}
    .ometa{display:flex;justify-content:space-between;font-size:10px;margin:2px 0;}
    .cust{font-size:11px;margin:4px 0;line-height:1.5;}
    .cust b{font-weight:800;}
    .col-hdr{display:flex;font-size:10px;font-weight:700;color:#555;}
    .col-hdr .cn{flex:1;} .col-hdr .cq{width:26px;text-align:center;} .col-hdr .cr{width:52px;text-align:right;} .col-hdr .ca{width:58px;text-align:right;}
    .irow{display:flex;align-items:flex-start;font-size:11.5px;margin:3px 0;}
    .irow .cn{flex:1;line-height:1.35;} .irow .cq{width:26px;text-align:center;flex-shrink:0;} .irow .cr{width:52px;text-align:right;color:#555;flex-shrink:0;} .irow .ca{width:58px;text-align:right;font-weight:700;flex-shrink:0;}
    .ht{font-size:9px;color:#999;}
    .trow{display:flex;justify-content:space-between;font-size:12px;font-weight:600;margin:3px 0;}
    .trow.disc{color:#16a34a;}
    .trow.grand{font-size:15px;font-weight:900;margin:5px 0 3px;}
    .trow.pay{font-size:10.5px;color:#555;font-weight:600;}
    .note{font-size:10px;color:#666;font-style:italic;margin-top:3px;}
    .ftr{text-align:center;font-size:10px;color:#555;margin-top:8px;line-height:1.55;}
    .ftr .pw{font-size:9px;color:#bbb;margin-top:2px;}
    @media print{body{width:100%;}}
  </style>
</head>
<body>
 
<div class="hdr">
  ${logoUrl ? `<img src="${logoUrl}" class="logo" alt=""/>` : ''}
  <div class="rname">${rName}</div>
  ${tagline ? `<div class="tagline">${tagline}</div>` : ''}
  ${address ? `<div class="rmeta">${address}</div>` : ''}
  ${phone   ? `<div class="rmeta">&#128222; ${phone}</div>` : ''}
  ${gstin   ? `<div class="gstin">GSTIN: ${gstin}</div>` : ''}
</div>
 
<div class="div-s"></div>
<div class="inv-lbl">Tax Invoice</div>
<div class="div-s"></div>
 
<div class="ometa">
  <span><b>Table:</b> ${order.tableLabel || '—'}</span>
  <span>${dateStr}</span>
</div>
${order.orderNumber ? `<div class="ometa"><span><b>Bill #</b> ${order.orderNumber}</span></div>` : ''}
 
${(order.customerName || order.customerPhone) ? `
<div class="cust">
  ${order.customerName  ? `<div><b>${order.customerName}</b></div>` : ''}
  ${order.customerPhone ? `<div>&#128241; ${order.customerPhone}</div>` : ''}
</div>` : ''}
 
<div class="div-d"></div>
 
<div class="col-hdr">
  <span class="cn">Item</span>
  <span class="cq">Qty</span>
  <span class="cr">Rate</span>
  <span class="ca">Amt</span>
</div>
<div class="div-d"></div>
 
${items.map(i => `
<div class="irow">
  <span class="cn">${i.name}${i.variant === 'half' ? ' <span class="ht">(&#189;)</span>' : ''}</span>
  <span class="cq">${i.qty}</span>
  <span class="cr">&#8377;${parseFloat(i.price).toFixed(2)}</span>
  <span class="ca">&#8377;${(parseFloat(i.price) * parseInt(i.qty)).toFixed(2)}</span>
</div>`).join('')}
 
<div class="div-d"></div>
 
<div class="trow"><span>Subtotal</span><span>&#8377;${parseFloat(order.subtotal || 0).toFixed(2)}</span></div>
 
${parseFloat(order.discountAmount || 0) > 0 ? `
<div class="trow disc"><span>Discount</span><span>–&#8377;${parseFloat(order.discountAmount).toFixed(2)}</span></div>` : ''}
 
<div class="trow">
  <span>GST (${order.gstRate || 5}%${order.gstType === 'inclusive' ? ' incl.' : ''})</span>
  <span>&#8377;${parseFloat(order.taxAmount || 0).toFixed(2)}</span>
</div>
 
<div class="div-s"></div>
 
<div class="trow grand">
  <span>TOTAL</span>
  <span>&#8377;${parseFloat(order.grandTotal || 0).toFixed(2)}</span>
</div>
 
<div class="trow pay">
  <span>Payment</span>
  <span>${(order.payMethod || 'cash').toUpperCase()}${order.payMethod === 'split'
    ? ` (Cash &#8377;${parseFloat(order.splitCash||0).toFixed(0)} · Card &#8377;${parseFloat(order.splitCard||0).toFixed(0)} · UPI &#8377;${parseFloat(order.splitUpi||0).toFixed(0)})`
    : ''
  }</span>
</div>
 
${order.orderNote ? `<div class="note">&#128221; ${order.orderNote}</div>` : ''}
 
<div class="div-d"></div>
 
<div class="ftr">
  <div>${footer}</div>
  <div class="pw">Powered by Restrowave POS</div>
</div>
 
<script>window.onload=()=>{window.print();window.onafterprint=()=>window.close();}<\/script>
</body>
</html>`;
 
  const w = window.open('', '_blank', 'width=360,height=660');
  if (w) { w.document.write(html); w.document.close(); }
}
 



/* ═══════════════════════════════════════════════════
   WHATSAPP / SMS BILL
═══════════════════════════════════════════════════ */
function sendBillWhatsApp(order, restaurantName = 'Restrowave Restaurant') {
  const items = typeof order.items === 'string' ? JSON.parse(order.items || '[]') : (order.items || []);
  const lines = items.map(i => `  ${i.name}${i.variant === 'half' ? '(½)' : ''} x${i.qty} = ₹${(parseFloat(i.price) * parseInt(i.qty)).toFixed(2)}`).join('\n');
  const msg = `🧾 *${restaurantName}*\nBill #${order.order_number}\n━━━━━━━━━━━━━━\n${lines}\n━━━━━━━━━━━━━━\nSubtotal: ₹${parseFloat(order.subtotal || 0).toFixed(2)}\nGST: ₹${parseFloat(order.tax_amount || 0).toFixed(2)}\n*Total: ₹${parseFloat(order.grand_total || 0).toFixed(2)}*\nPayment: ${(order.payment_method || 'cash').toUpperCase()}\n\nThank you! 🙏`;
  const phone = (order.customer_phone || '').replace(/\D/g, '');
  const url   = `https://wa.me/${phone ? `91${phone}` : ''}?text=${encodeURIComponent(msg)}`;
  window.open(url, '_blank');
}

/* ═══════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════ */
export default function Order() {
  const [categories,     setCategories]     = useState([]);
  const [menuItems,      setMenuItems]      = useState([]);
  const [tables,         setTables]         = useState([]);
  const [menuLoading,    setMenuLoading]    = useState(true);
  const [rightMode,      setRightMode]      = useState('cart');
  const [openOrders,     setOpenOrders]     = useState([]);
  const [kitchenTickets, setKitchenTickets] = useState([]);
  const [ordersLoading,  setOrdersLoading]  = useState(false);
  const [kitchenLoading, setKitchenLoading] = useState(false);
  const [activeCat,      setActiveCat]      = useState('all');
  const [search,         setSearch]         = useState('');
  const [showTableModal, setShowTableModal] = useState(false);
  const [showDiscount,   setShowDiscount]   = useState(false);
  const [placing,        setPlacing]        = useState(false);
  const [orderSuccess,   setOrderSuccess]   = useState(null);
  const [isOnline,       setIsOnline]       = useState(navigator.onLine);
  const [syncingQ,       setSyncingQ]       = useState(false);
  const [offlineCount,   setOfflineCount]   = useState(0);
  const [updateSyncCount,setUpdateSyncCount]= useState(0);
  const [voidingId,      setVoidingId]      = useState(null);
  const [completingId,   setCompletingId]   = useState(null);
  const [showBillModal,  setShowBillModal]  = useState(null); // order to show bill
  const [cancelModalOrder, setCancelModalOrder] = useState(null);
  const [cancelReason,   setCancelReason]   = useState('');
  const [restaurantName, setRestaurantName] = useState(localStorage.getItem('restaurant_name') || 'Restrowave Restaurant');

  const [slots, dispatch] = useReducer(slotsReducer, INIT_SLOTS);
  const activeSlot = slots.byKey[slots.activeKey] || null;

  const { toast, fire } = useToast();
  const syncRef        = useRef(false);
  const kitchenPollRef = useRef(null);
  const restoredRef    = useRef(false);

const [loading,          setLoading]          = useState(true);
const [zones,            setZones]            = useState([]);

  const [restaurantProfile, setRestaurantProfile] = useState({
  name:        localStorage.getItem('restaurant_name') || 'Restaurant',
  tagline:     '',
  phone:       '',
  address:     '',
  city:        '',
  state:       '',
  pincode:     '',
  gst_number:  '',
  logo_url:    '',
  footer_note: '',
});



const loadData = async () => {
  setLoading(true);
  try {
    const [zonesRes, tablesRes, profileRes] = await Promise.all([
      api().get('/zones'),
      api().get('/tables'),
      api().get('/settings/profile').catch(() => ({ data: { restaurant: {} } })),
    ]);
    setZones(zonesRes.data.zones || []);
    setTables((tablesRes.data.tables || []).map(t => ({
      ...t,
      items:   [],
      guests:  t.guests  || 0,
      timer:   t.timer   || null,
      groupId: t.group_id || t.groupId || null,
    })));
 
    const prof = profileRes.data.restaurant || {};
    if (prof.name) {
      setRestaurantName(prof.name);
      localStorage.setItem('restaurant_name', prof.name);
    }
    setRestaurantProfile(prev => ({ ...prev, ...prof }));
 
  } catch { fire('Failed to load tables', 'error'); }
  finally { setLoading(false); }
};
 


  /* ── kitchen queue block: if ANY ticket is pending for the active table slot, block billing ── */
  const activeTableId = activeSlot?.slotType === 'table' ? activeSlot.tableId : null;
  const hasKitchenQueueForActiveSlot = useMemo(() => {
    if (!activeSlot?.sentToKitchen) return false;
    // Check if there are pending kitchen tickets for this slot
    return kitchenTickets.some(t =>
      t.slot_key === slots.activeKey ||
      (activeTableId && String(t.table_id) === String(activeTableId))
    );
  }, [kitchenTickets, slots.activeKey, activeTableId, activeSlot]);

  /* ── Online/offline ── */
  useEffect(() => {
    const up = () => { setIsOnline(true); syncOffline(); };
    const dn = () => setIsOnline(false);
    window.addEventListener('online', up); window.addEventListener('offline', dn);
    setOfflineCount(loadQ().length); setUpdateSyncCount(loadUQ().length);
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', dn); };
  }, []);

  /* ── Restore slots on mount ── */
  useEffect(() => {
    if (restoredRef.current) return; restoredRef.current = true;
    const saved = loadPersistedSlots();
    if (saved?.order?.length > 0) {
      saved.order.forEach(key => { const slot = saved.byKey[key]; if (slot) dispatch({ type: 'LOAD_FOR_EDIT', key, slot }); });
      if (saved.activeKey) dispatch({ type: 'SET_ACTIVE', key: saved.activeKey });
    }
  }, []);

  /* ── Persist slots ── */
  useEffect(() => { slots.order.length > 0 ? persistSlots(slots) : clearPersistedSlots(); }, [slots]);

  /* ── Load menu + tables ── */
  useEffect(() => { loadMenu(); loadTables(); }, []);

  /* ── Kitchen poll ── */
  useEffect(() => {
    if (rightMode === 'kitchen') { loadKitchenTickets(); kitchenPollRef.current = setInterval(loadKitchenTickets, 30000); }
    return () => clearInterval(kitchenPollRef.current);
  }, [rightMode]);

  /* ── Open orders on panel open ── */
  useEffect(() => { if (rightMode === 'open_orders') loadOpenOrders(); }, [rightMode]);

  /* ── Also load kitchen tickets in background to check blocking ── */
  useEffect(() => {
    loadKitchenTickets();
    const interval = setInterval(loadKitchenTickets, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadMenu = async () => {
    setMenuLoading(true);
    try {
      const [catR, itemR] = await Promise.all([
        api().get('/categories').catch(() => ({ data: { categories: [] } })),
        api().get('/items').catch(()       => ({ data: { items: [] } })),
      ]);
      setCategories(catR.data.categories || []);
      setMenuItems((itemR.data.items || itemR.data.products || [])
        .map(p => ({ ...p, tags: Array.isArray(p.tags) ? p.tags : (p.tags ? JSON.parse(p.tags) : []), is_available: Boolean(p.is_available !== undefined ? p.is_available : true), has_half_plate: Boolean(p.has_half_plate), price_full: parseFloat(p.price_full ?? p.price ?? 0), price_half: p.price_half ? parseFloat(p.price_half) : null }))
        .filter(p => p.is_available));
    } catch { fire('Failed to load menu', 'error'); }
    finally { setMenuLoading(false); }
  };

  const loadTables = async () => {
    try { const r = await api().get('/tables').catch(() => ({ data: { tables: [] } })); setTables(r.data.tables || []); } catch {}
  };

  const loadOpenOrders = async () => {
    setOrdersLoading(true);
    try {
      const results = await Promise.all(
        ['placed', 'kot', 'preparing', 'ready'].map(s =>
          api().get(`/orders?status=${s}&limit=50&days=3`).catch(() => ({ data: { orders: [] } }))
        )
      );
      setOpenOrders(results.flatMap(r => r.data.orders || []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } finally { setOrdersLoading(false); }
  };

  const loadKitchenTickets = async () => {
    setKitchenLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api().get('/kitchen?status=pending').catch(()      => ({ data: { tickets: [] } })),
        api().get('/kitchen?status=acknowledged').catch(() => ({ data: { tickets: [] } })),
      ]);
      setKitchenTickets([...(r1.data.tickets || []), ...(r2.data.tickets || [])]);
    } finally { setKitchenLoading(false); }
  };

  /* ── Filtered menu ── */
  const filtered = useMemo(() =>
    menuItems.filter(p =>
      (activeCat === 'all' || p.category === activeCat || String(p.category_id) === activeCat) &&
      p.name.toLowerCase().includes(search.toLowerCase())
    ), [menuItems, activeCat, search]);

  /* ── Slot dispatchers ── */
  const openTableSlot    = table => { dispatch({ type: 'ADD_SLOT', key: `table_${table.id}`, slotType: 'table', tableId: table.id, tableMeta: table }); setShowTableModal(false); setRightMode('cart'); };
  const openDeliverySlot = type  => { dispatch({ type: 'ADD_SLOT', key: `${type}_${newTempId()}`, slotType: type }); setRightMode('cart'); };
  const removeSlot       = k     => dispatch({ type: 'REMOVE_SLOT', key: k });
  const patch            = useCallback(p => { if (slots.activeKey) dispatch({ type: 'PATCH_SLOT', key: slots.activeKey, patch: p }); }, [slots.activeKey]);
  const addItem          = useCallback((product, variant = 'full') => {
    if (!slots.activeKey) { fire('Select an order slot first', 'error'); return; }
    dispatch({ type: 'ADD_ITEM', key: slots.activeKey, item: { id: product.id, name: product.name, variant, price: variant === 'half' ? product.price_half : product.price_full, image_url: product.image_url, prep_time: product.prep_time } });
  }, [slots.activeKey]);
  const updateQty  = (cid, d) => dispatch({ type: 'UPDATE_QTY',    key: slots.activeKey, cartId: cid, delta: d });
  const removeItem = cid      => dispatch({ type: 'REMOVE_ITEM',   key: slots.activeKey, cartId: cid });
  const setNote    = (cid, n) => dispatch({ type: 'SET_ITEM_NOTE', key: slots.activeKey, cartId: cid, note: n });
  const clearSlot  = ()       => dispatch({ type: 'CLEAR_SLOT',    key: slots.activeKey });

  /* ── Bill ── */
  const bill       = useMemo(() => activeSlot ? calcBill(activeSlot) : null, [activeSlot]);
  const splitTotal = useMemo(() => activeSlot ? (parseFloat(activeSlot.splitCash) || 0) + (parseFloat(activeSlot.splitCard) || 0) + (parseFloat(activeSlot.splitUpi) || 0) : 0, [activeSlot]);

  const newSinceKot = useMemo(() => {
    if (!activeSlot) return [];
    if (!activeSlot.sentToKitchen) return activeSlot.items;
    return activeSlot.items.map(i => { const prev = activeSlot.kotItems.find(k => k.cartId === i.cartId); if (!prev) return i; const diff = i.qty - prev.qty; return diff > 0 ? { ...i, qty: diff } : null; }).filter(Boolean);
  }, [activeSlot]);

  /* ── Validate before placing ── */
  const validateSlot = (slot, status) => {
    if (!slot?.items.length) return 'Add items first';
    if (slot.slotType === 'table' && !slot.tableId) return 'Select a table';
    // Name + phone mandatory for all order types
    if (!slot.customerName?.trim()) return 'Customer name is required';
    if (!slot.customerPhone?.trim()) return 'Customer phone is required';
    if (slot.payMethod === 'split' && bill && Math.abs(splitTotal - bill.grandTotal) > 1) return `Split must equal ₹${bill.grandTotal.toFixed(2)}`;
    return null;
  };

  /* ── Send to kitchen ── */
  const handleSendKitchen = async () => {
    if (!activeSlot || newSinceKot.length === 0) { fire('No new items to send', 'error'); return; }
    const key = slots.activeKey;
    try {
      if (isOnline) await api().post('/kitchen', { slot_key: key, order_type: activeSlot.slotType, table_id: activeSlot.tableId || null, table_label: activeSlot.tableMeta?.label || null, items: newSinceKot.map(i => ({ name: i.name, variant: i.variant, qty: i.qty, price: i.price, note: i.note || null })) });
      dispatch({ type: 'MARK_KOT', key });
      fire('Sent to kitchen 👨‍🍳');
      loadKitchenTickets();
    } catch { dispatch({ type: 'MARK_KOT', key }); fire('Kitchen send failed — marked locally', 'error'); }
  };

  /* ── Build payload ── */
  const buildPayload = (slot, status, tempId) => {
    const b = calcBill(slot);
    return {
      tempId, order_type: slot.slotType,
      table_id: slot.tableId || null, table_label: slot.tableMeta?.label || null,
      customer_name: slot.customerName || null, customer_phone: slot.customerPhone || null,
      external_order_id: slot.externalOrderId || null, order_note: slot.orderNote || null,
      payment_method: slot.payMethod,
      split_cash: slot.payMethod === 'split' ? parseFloat(slot.splitCash) || 0 : null,
      split_card: slot.payMethod === 'split' ? parseFloat(slot.splitCard) || 0 : null,
      split_upi:  slot.payMethod === 'split' ? parseFloat(slot.splitUpi)  || 0 : null,
      discount_type: slot.discountVal ? slot.discountType : null,
      discount_value: parseFloat(slot.discountVal) || 0,
      discount_amount: b.discountAmount, delivery_charge: b.delivery,
      subtotal: b.subtotal, tax_amount: b.taxAmount, grand_total: b.grandTotal,
      gst_rate: b.gstRate, gst_type: b.gstType,
      status,
      items: slot.items.map(i => ({ menu_item_id: i.id, name: i.name, variant: i.variant, price: i.price, qty: i.qty, total: i.price * i.qty, note: i.note || null })),
    };
  };

  /* ── Place order ── */
  const handlePlace = async (status = 'placed') => {
    const err = validateSlot(activeSlot, status);
    if (err) { fire(err, 'error'); return; }

    // Block if kitchen queue is open for this slot
    if (hasKitchenQueueForActiveSlot && (status === 'placed' || status === 'placed_print')) {
      fire('Kitchen order is still in queue — complete kitchen first', 'error'); return;
    }

    setPlacing(true);
    const tempId  = newTempId();
    const slotKey = slots.activeKey;
    const payload = buildPayload(activeSlot, status, tempId);

    try {
      if (!isOnline) throw new Error('offline');
      const r      = await api().post('/orders', payload);
      const placed = r.data.order;

      if (status === 'placed_print') {
  printTableBill({
    ...placed,
    items: activeSlot.items,              // placed API response may not have items array
    orderNumber: placed.order_number,
    tableLabel:  placed.table_label || activeSlot.tableMeta?.label,
    customerName:  activeSlot.customerName,
    customerPhone: activeSlot.customerPhone,
    orderNote:     activeSlot.orderNote,
    payMethod:     activeSlot.payMethod,
    splitCash:     activeSlot.splitCash,
    splitCard:     activeSlot.splitCard,
    splitUpi:      activeSlot.splitUpi,
    subtotal:      placed.subtotal   || bill?.subtotal,
    taxAmount:     placed.tax_amount || bill?.taxAmount,
    grandTotal:    placed.grand_total || bill?.grandTotal,
    discountAmount:placed.discount_amount || bill?.discountAmount,
    gstRate:       placed.gst_rate   || bill?.gstRate,
    gstType:       placed.gst_type   || bill?.gstType,
  }, restaurantProfile);
}

      setOrderSuccess(placed);
      fire(`Order #${placed.order_number} placed! 🎉`);

      if (activeSlot.slotType === 'table' && activeSlot.tableId) {
        const freesTable = ['placed', 'placed_print', 'served'].includes(status);
        setTables(prev => prev.map(t => String(t.id) === String(activeSlot.tableId) ? { ...t, status: freesTable ? 'available' : 'occupied' } : t));
      }
      dispatch({ type: 'REMOVE_SLOT', key: slotKey });
    } catch (err) {
      addQ(payload); setOfflineCount(loadQ().length);
      fire(isOnline ? (err.response?.data?.message || 'Server error — saved offline') : 'Offline — saved locally ☁️', 'error');
      dispatch({ type: 'REMOVE_SLOT', key: slotKey });
    } finally { setPlacing(false); }
  };

  /* ── Sync offline ── */
  const syncOffline = async () => {
    if (syncRef.current) return;
    syncRef.current = true; setSyncingQ(true); let done = 0;
    for (const p of loadQ()) { try { await api().post('/orders', p); removeQ(p.tempId); done++; } catch (e) { if (e.response?.status === 409) { removeQ(p.tempId); done++; } } }
    for (const p of loadUQ()) { try { await api().put(`/orders/${p.orderId}`, p); removeUQ(p.updateTempId); done++; } catch (e) { if (e.response?.status === 409) { removeUQ(p.updateTempId); done++; } } }
    syncRef.current = false; setSyncingQ(false);
    setOfflineCount(loadQ().length); setUpdateSyncCount(loadUQ().length);
    if (done > 0) fire(`${done} item(s) synced ✓`);
  };

  /* ── Complete order ── */
  const handleCompleteOrder = async order => {
    setCompletingId(order.id);
    try {
      await api().put(`/orders/${order.id}/status`, { status: 'served', payment_status: 'paid' });
      fire(`Order #${order.order_number} completed ✓`);
      setOpenOrders(prev => prev.filter(o => o.id !== order.id));
      setTables(prev => prev.map(t => String(t.id) === String(order.table_id) ? { ...t, status: 'available' } : t));
    } catch { fire('Failed to complete order', 'error'); }
    finally { setCompletingId(null); }
  };

  /* ── Cancel order with reason ── */
  const handleCancelOrder = async () => {
    if (!cancelModalOrder) return;
    if (!cancelReason.trim()) { fire('Please enter a cancellation reason', 'error'); return; }
    try {
      await api().put(`/orders/${cancelModalOrder.id}/status`, { status: 'cancelled', cancelled_reason: cancelReason.trim() });
      fire(`Order #${cancelModalOrder.order_number} cancelled`);
      setOpenOrders(prev => prev.filter(o => o.id !== cancelModalOrder.id));
      setTables(prev => prev.map(t => String(t.id) === String(cancelModalOrder.table_id) ? { ...t, status: 'available' } : t));
      setCancelModalOrder(null); setCancelReason('');
    } catch { fire('Failed to cancel order', 'error'); }
  };

  /* ── Void (same as cancel but from open orders panel directly) ── */
  const handleVoidOrder = order => { setCancelModalOrder(order); setCancelReason(''); };

  /* ── Modify order ── */
  const handleModifyOrder = order => {
    const slotKey  = `edit_${order.id}`;
    const rawItems = typeof order.items === 'string' ? JSON.parse(order.items || '[]') : (order.items || []);
    const items    = rawItems.map(i => ({ cartId: `${i.menu_item_id || i.name}_${i.variant || 'full'}`, id: i.menu_item_id || null, name: i.name, variant: i.variant || 'full', price: parseFloat(i.price), qty: parseInt(i.qty), note: i.note || '' }));
    dispatch({
      type: 'LOAD_FOR_EDIT', key: slotKey,
      slot: { ...BLANK_SLOT, slotKey, slotType: order.order_type, tableId: order.table_id || null, tableMeta: order.table_label ? { label: order.table_label } : null, customerName: order.customer_name || '', customerPhone: order.customer_phone || '', orderNote: order.order_note || '', payMethod: order.payment_method || 'cash', items, editOrderId: order.id, editOrderNumber: order.order_number },
    });
    setRightMode('cart');
  };

  /* ── Save modification ── */
  const handleSaveModify = async () => {
    if (!activeSlot?.editOrderId) return handlePlace('placed');
    if (!activeSlot.items.length) { fire('Add at least one item', 'error'); return; }
    setPlacing(true);
    const b            = calcBill(activeSlot);
    const updateTempId = newTempId();
    const payload = {
      updateTempId, orderId: activeSlot.editOrderId,
      order_note: activeSlot.orderNote || null, payment_method: activeSlot.payMethod,
      discount_type: activeSlot.discountVal ? activeSlot.discountType : null,
      discount_value: parseFloat(activeSlot.discountVal) || 0,
      discount_amount: b.discountAmount, delivery_charge: b.delivery,
      subtotal: b.subtotal, tax_amount: b.taxAmount, grand_total: b.grandTotal,
      gst_rate: b.gstRate, gst_type: b.gstType,
      items: activeSlot.items.map(i => ({ menu_item_id: i.id, name: i.name, variant: i.variant, price: i.price, qty: i.qty, total: i.price * i.qty, note: i.note || null })),
    };
    try {
      if (!isOnline) throw new Error('offline');
      await api().put(`/orders/${activeSlot.editOrderId}`, payload);
      fire(`Order #${activeSlot.editOrderNumber} updated`);
      dispatch({ type: 'REMOVE_SLOT', key: slots.activeKey });
      loadOpenOrders();
    } catch (err) {
      addUQ(payload); setUpdateSyncCount(loadUQ().length);
      fire(isOnline ? (err.response?.data?.message || 'Server error — update saved offline') : 'Offline — update saved locally ☁️', 'error');
      dispatch({ type: 'REMOVE_SLOT', key: slots.activeKey });
    } finally { setPlacing(false); }
  };

  /* ── Kitchen ticket done ── */
  const handleTicketDone = async ticketId => {
    try {
      await api().put(`/kitchen/${ticketId}`, { status: 'done' });
      setKitchenTickets(prev => prev.filter(t => t.id !== ticketId));
      fire('Ticket marked done');
    } catch { fire('Failed to update ticket', 'error'); }
  };

  /* ── Helpers ── */
  const isEditMode     = activeSlot?.editOrderId != null;
  const typeInfo       = ORDER_TYPES.find(t => activeSlot && t.id === activeSlot.slotType) || ORDER_TYPES[0];
  const needsDelivery  = activeSlot && ['zomato', 'swiggy', 'other'].includes(activeSlot.slotType);
  const tableSlotKeys  = slots.order.filter(k => k.startsWith('table_'));
  const otherSlotKeys  = slots.order.filter(k => !k.startsWith('table_') && !k.startsWith('edit_'));
  const editSlotKeys   = slots.order.filter(k => k.startsWith('edit_'));
  const pickableTables = useMemo(() => tables.filter(t => t.is_active && ['available', 'occupied', 'reserved'].includes(t.status)), [tables]);
  const openTableIds   = useMemo(() => tableSlotKeys.map(k => k.replace('table_', '')), [tableSlotKeys]);
  const totalOffline   = offlineCount + updateSyncCount;

  /* ═══════════════════════════════════════════════════
     RENDER
  ═══════════════════════════════════════════════════ */
  return (
    <div className="order-page-container">
      <Navbar />
      <main className="order-layout">

        {/* ═══ LEFT — Category Sidebar ═══ */}
        <aside className="cat-sidebar">
          <button className={`cat-tab ${activeCat === 'all' ? 'active' : ''}`} onClick={() => setActiveCat('all')}>
            <span className="cat-emoji">🍽️</span><span className="cat-name">All</span>
          </button>
          {categories.map(c => (
            <button key={c.id} className={`cat-tab ${activeCat === String(c.id) ? 'active' : ''}`} onClick={() => setActiveCat(String(c.id))}>
              <span className="cat-emoji">{c.icon || '🍴'}</span><span className="cat-name">{c.name}</span>
            </button>
          ))}
        </aside>

        {/* ═══ CENTER ═══ */}
        <section className="product-section">
          <header className="order-header">
            <div className="search-bar">
              <Search size={14}/>
              <input placeholder="Search menu…" value={search} onChange={e => setSearch(e.target.value)}/>
              {search && <button onClick={() => setSearch('')} style={{ background:'none',border:'none',cursor:'pointer',display:'flex' }}><X size={11}/></button>}
            </div>
            <div className="conn-bar">
              {isOnline ? <span className="conn-pill online"><Wifi size={10}/> Live</span> : <span className="conn-pill offline"><WifiOff size={10}/> Offline</span>}
              {totalOffline > 0 && (
                <button className="sync-btn" onClick={syncOffline} disabled={!isOnline || syncingQ}>
                  {syncingQ ? <Loader2 size={10} className="mn-spin"/> : <RefreshCw size={10}/>} Sync {totalOffline}
                </button>
              )}
            </div>
          </header>

          {/* Slot tabs */}
          <div className="slot-tabs-bar">
            {tableSlotKeys.map(key => {
              const s = slots.byKey[key]; const tbl = tables.find(t => String(t.id) === String(s.tableId));
              return (
                <button key={key} className={`slot-tab slot-table ${slots.activeKey === key ? 'active' : ''}`}
                  onClick={() => { dispatch({ type: 'SET_ACTIVE', key }); setRightMode('cart'); }}>
                  <Table2 size={10}/> {tbl ? `T-${tbl.label}` : 'Table'}
                  {s.items.length > 0 && <span className="slot-count">{s.items.length}</span>}
                  {s.sentToKitchen && <ChefHat size={9} style={{ color: '#22c55e' }}/>}
                  <span className="slot-close" onClick={e => { e.stopPropagation(); removeSlot(key); }}><X size={8}/></span>
                </button>
              );
            })}
            {otherSlotKeys.map(key => {
              const s = slots.byKey[key]; const info = ORDER_TYPES.find(t => t.id === s.slotType);
              return (
                <button key={key} className={`slot-tab ${slots.activeKey === key ? 'active' : ''}`} style={{ '--sc': info?.color || '#666' }}
                  onClick={() => { dispatch({ type: 'SET_ACTIVE', key }); setRightMode('cart'); }}>
                  {info?.icon} {info?.label || s.slotType}
                  {s.items.length > 0 && <span className="slot-count">{s.items.length}</span>}
                  <span className="slot-close" onClick={e => { e.stopPropagation(); removeSlot(key); }}><X size={8}/></span>
                </button>
              );
            })}
            {editSlotKeys.map(key => {
              const s = slots.byKey[key];
              return (
                <button key={key} className={`slot-tab slot-edit ${slots.activeKey === key ? 'active' : ''}`}
                  onClick={() => { dispatch({ type: 'SET_ACTIVE', key }); setRightMode('cart'); }}>
                  <Edit3 size={10}/> #{s.editOrderNumber}
                  {s.items.length > 0 && <span className="slot-count">{s.items.length}</span>}
                  <span className="slot-close" onClick={e => { e.stopPropagation(); removeSlot(key); }}><X size={8}/></span>
                </button>
              );
            })}
            <div className="slot-new-group">
              <button className="slot-new-btn table-btn" onClick={() => setShowTableModal(true)}><Table2 size={11}/> Table</button>
              {ORDER_TYPES.filter(t => t.id !== 'table').map(t => (
                <button key={t.id} className={`slot-new-btn ${t.comingSoon ? 'coming-soon' : ''}`} style={{ '--sc': t.color }}
                  onClick={() => !t.comingSoon && openDeliverySlot(t.id)} disabled={t.comingSoon}
                  title={t.comingSoon ? `${t.label} — Coming Soon` : `New ${t.label} order`}>
                  {t.icon}{t.comingSoon && <span style={{ fontSize:7,marginLeft:1,opacity:0.7 }}>Soon</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Customer + name/phone (always shown for any slot type) */}
          {activeSlot && !isEditMode && (
            <div className="customer-bar">
              <div className="cbar-field req">
                <User size={11}/>
                <input placeholder="Customer name *" value={activeSlot.customerName} onChange={e => patch({ customerName: e.target.value })}/>
              </div>
              <div className="cbar-field req">
                <Phone size={11}/>
                <input placeholder="Phone *" value={activeSlot.customerPhone} onChange={e => patch({ customerPhone: e.target.value })}/>
              </div>
              {['zomato','swiggy','other'].includes(activeSlot.slotType) && (
                <div className="cbar-field"><Hash size={11}/><input placeholder="Platform order ID" value={activeSlot.externalOrderId} onChange={e => patch({ externalOrderId: e.target.value })}/></div>
              )}
            </div>
          )}

          {/* No slot */}
          {!activeSlot && (
            <div className="no-slot-hint">
              <ShoppingCart size={34} style={{ opacity:0.3 }}/>
              <p>Open a new order to start</p>
              <div style={{ display:'flex',gap:7,flexWrap:'wrap',justifyContent:'center',marginTop:8 }}>
                <button className="slot-new-btn table-btn" onClick={() => setShowTableModal(true)}><Table2 size={11}/> New Table</button>
                {ORDER_TYPES.filter(t => t.id !== 'table' && !t.comingSoon).map(t => (
                  <button key={t.id} className="slot-new-btn" style={{ '--sc':t.color }} onClick={() => openDeliverySlot(t.id)}>{t.icon} {t.label}</button>
                ))}
              </div>
            </div>
          )}

          {/* Product grid */}
          {activeSlot && (menuLoading
            ? <div style={{ display:'flex',justifyContent:'center',alignItems:'center',flex:1,gap:8,color:'var(--muted)' }}><Loader2 size={22} className="mn-spin"/> Loading…</div>
            : filtered.length === 0
              ? <div className="order-empty"><span style={{ fontSize:34 }}>🍽️</span><p>No items found</p></div>
              : <div className="order-grid">{filtered.map(p => <MenuCard key={p.id} product={p} onAdd={addItem}/>)}</div>
          )}
        </section>

        {/* ═══ RIGHT — Panel ═══ */}
        <aside className="billing-sidebar">
          <div className="panel-mode-bar">
            {[
              { id:'cart',        icon:<ShoppingCart size={12}/>, label:'Cart' },
              { id:'open_orders', icon:<ListOrdered  size={12}/>, label:'Orders' },
              { id:'kitchen',     icon:<ChefHat      size={12}/>, label:'Kitchen' },
            ].map(m => (
              <button key={m.id} className={`pmode-btn ${rightMode === m.id ? 'active' : ''}`} onClick={() => setRightMode(m.id)}>
                {m.icon} {m.label}
                {m.id === 'open_orders' && openOrders.length > 0 && <span className="slot-count" style={{ background:'rgba(255,140,0,0.22)',color:'#ff8c00' }}>{openOrders.length}</span>}
                {m.id === 'kitchen' && kitchenTickets.length > 0 && <span className="slot-count" style={{ background:'rgba(239,68,68,0.22)',color:'#ef4444' }}>{kitchenTickets.length}</span>}
              </button>
            ))}
          </div>

          {/* ── CART ── */}
          {rightMode === 'cart' && (
            !activeSlot
              ? <div style={{ flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:8,color:'var(--muted)',padding:20,textAlign:'center' }}>
                  <Receipt size={34} style={{ opacity:0.3 }}/><p style={{ fontWeight:700,fontSize:12 }}>No active order</p><span style={{ fontSize:11 }}>Select a slot above</span>
                </div>
              : <>
                  <div className="bill-header">
                    <div className="bill-title">
                      <div className="bill-type-dot" style={{ background:typeInfo.color }}/>
                      <h3>{isEditMode ? `Edit #${activeSlot.editOrderNumber}` : activeSlot.slotType === 'table' && activeSlot.tableMeta ? `Table ${activeSlot.tableMeta.label}` : typeInfo.label}</h3>
                      {activeSlot.sentToKitchen && newSinceKot.length === 0 && <span className="kot-badge"><ChefHat size={9}/> KOT</span>}
                      {hasKitchenQueueForActiveSlot && <span className="kitchen-queue-warn"><AlertTriangle size={9}/> Kitchen busy</span>}
                    </div>
                    <button className="clear-btn" onClick={clearSlot}><Trash2 size={13}/></button>
                  </div>

                  <div className="cart-items">
                    {activeSlot.items.length === 0
                      ? <div className="empty-cart"><Receipt size={34}/><p>No items</p><span>Tap to add</span></div>
                      : activeSlot.items.map(item => {
                          const isNew    = !activeSlot.kotItems.find(k => k.cartId === item.cartId);
                          const prevQ    = activeSlot.kotItems.find(k => k.cartId === item.cartId)?.qty || 0;
                          const addedQty = item.qty - prevQ;
                          return <CartRow key={item.cartId} item={item} isNew={isNew || addedQty > 0} addedQty={addedQty > 0 && !isNew ? addedQty : null} kotSent={activeSlot.sentToKitchen} onInc={() => updateQty(item.cartId, +1)} onDec={() => updateQty(item.cartId, -1)} onRemove={() => removeItem(item.cartId)} onNote={n => setNote(item.cartId, n)}/>;
                        })}
                  </div>

                  {activeSlot.items.length > 0 && (
                    <div className="order-note-wrap">
                      <MessageSquare size={10}/>
                      <input placeholder="Order note…" value={activeSlot.orderNote} onChange={e => patch({ orderNote: e.target.value })}/>
                    </div>
                  )}

                  {/* Kitchen queue warning banner */}
                  {hasKitchenQueueForActiveSlot && (
                    <div className="kitchen-block-banner">
                      <AlertTriangle size={13}/>
                      <span>Kitchen order is still in queue. Complete kitchen preparation before billing.</span>
                    </div>
                  )}

                  <div className="bill-footer">
                    {newSinceKot.length > 0 && !isEditMode && (
                      <button className="kitchen-btn" onClick={handleSendKitchen}>
                        <ChefHat size={13}/>{activeSlot.sentToKitchen ? `+${newSinceKot.length} to Kitchen` : `Send to Kitchen (${newSinceKot.length})`}
                      </button>
                    )}
                    {bill && (
                      <>
                        <div className="summary-row"><span>Subtotal</span><span>₹{bill.subtotal.toFixed(2)}</span></div>
                        <div className="discount-row">
                          <button className="discount-toggle" onClick={() => setShowDiscount(v => !v)}>
                            <Tag size={10}/>{bill.discountAmount > 0 ? `Disc −₹${bill.discountAmount.toFixed(2)}` : 'Add Discount'}
                            <ChevronDown size={9} style={{ transform:showDiscount?'rotate(180deg)':'none',transition:'0.2s',marginLeft:'auto' }}/>
                          </button>
                          {showDiscount && (
                            <div className="discount-box">
                              <div className="discount-type-toggle">
                                {[['percent','%'],['flat','₹']].map(([id,lbl]) => (
                                  <button key={id} className={activeSlot.discountType === id ? 'active' : ''} onClick={() => patch({ discountType:id })}>{lbl}</button>
                                ))}
                              </div>
                              <input type="number" min="0" className="discount-input" placeholder={activeSlot.discountType === 'percent' ? '10' : '50'} value={activeSlot.discountVal} onChange={e => patch({ discountVal:e.target.value })}/>
                            </div>
                          )}
                        </div>
                        {bill.discountAmount > 0 && <div className="summary-row"><span style={{ color:'#22c55e',display:'flex',alignItems:'center',gap:3 }}><Tag size={9}/> Discount</span><span style={{ color:'#22c55e' }}>−₹{bill.discountAmount.toFixed(2)}</span></div>}
                        <div className="gst-selector-row">
                          <div style={{ display:'flex',alignItems:'center',gap:4 }}>
                            <span style={{ fontSize:9.5,fontWeight:700,color:'var(--muted)',flexShrink:0 }}>GST</span>
                            <div style={{ display:'flex',gap:2 }}>
                              {GST_RATES.map(r => (
                                <button key={r} onClick={() => patch({ gstRate:r })}
                                  style={{ padding:'2px 5px',borderRadius:4,border:'1px solid',fontSize:8.5,fontWeight:700,cursor:'pointer',background:activeSlot.gstRate===r?'#ff8c00':'#fff',borderColor:activeSlot.gstRate===r?'#ff8c00':'rgba(0,0,0,0.10)',color:activeSlot.gstRate===r?'#fff':'var(--muted)' }}>
                                  {r}%
                                </button>
                              ))}
                            </div>
                          </div>
                          <div style={{ display:'flex',gap:2,marginLeft:'auto' }}>
                            {[['exclusive','Excl.'],['inclusive','Incl.']].map(([id,lbl]) => (
                              <button key={id} onClick={() => patch({ gstType:id })}
                                style={{ padding:'2px 6px',borderRadius:4,border:'1px solid',fontSize:8.5,fontWeight:700,cursor:'pointer',background:activeSlot.gstType===id?'#6366f1':'#fff',borderColor:activeSlot.gstType===id?'#6366f1':'rgba(0,0,0,0.10)',color:activeSlot.gstType===id?'#fff':'var(--muted)' }}>
                                {lbl}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="summary-row">
                          <span>GST {bill.gstRate}% <span style={{ fontSize:8,opacity:0.6 }}>({bill.gstType === 'inclusive' ? 'incl.' : 'excl.'})</span></span>
                          <span>₹{bill.taxAmount.toFixed(2)}</span>
                        </div>
                        {needsDelivery && (
                          <div className="summary-row delivery-row">
                            <span><Bike size={9}/> Delivery</span>
                            <input type="number" min="0" placeholder="0" className="delivery-input" value={activeSlot.deliveryCharge} onChange={e => patch({ deliveryCharge:e.target.value })}/>
                          </div>
                        )}
                        <div className="total-row"><span>Grand Total</span><span>₹{bill.grandTotal.toFixed(2)}</span></div>
                        <div className="payment-methods">
                          {PAYMENT_METHODS.map(pm => (
                            <button key={pm.id} className={`pm-btn ${activeSlot.payMethod === pm.id ? 'active' : ''}`} onClick={() => patch({ payMethod:pm.id })}>{pm.icon} {pm.label}</button>
                          ))}
                        </div>
                        {activeSlot.payMethod === 'split' && (
                          <div className="split-wrap">
                            {[['splitCash','Cash',<Banknote size={10}/>],['splitCard','Card',<CreditCard size={10}/>],['splitUpi','UPI',<Smartphone size={10}/>]].map(([k,lbl,ico]) => (
                              <div key={k} className="split-row">{ico}<span>{lbl}</span><input type="number" min="0" placeholder="0" value={activeSlot[k]} onChange={e => patch({ [k]:e.target.value })}/></div>
                            ))}
                            <div className={`split-balance ${Math.abs(splitTotal-bill.grandTotal)<0.01?'ok':'mismatch'}`}>
                              {Math.abs(splitTotal-bill.grandTotal)<0.01?<><CheckCircle2 size={9}/> Balanced</>:<>Rem: ₹{(bill.grandTotal-splitTotal).toFixed(2)}</>}
                            </div>
                          </div>
                        )}

                        {isEditMode ? (
                          <button className="checkout-btn" disabled={placing||!activeSlot.items.length} onClick={handleSaveModify} style={{ marginTop:4 }}>
                            {placing?<><Loader2 size={12} className="mn-spin"/> Saving…</>:<><CheckCircle2 size={12}/> Save Changes</>}
                          </button>
                        ) : (
                          <>
                            <div className="checkout-actions">
                              <button className="kot-btn" disabled={placing||!activeSlot.items.length} onClick={() => handlePlace('kot')}><Printer size={12}/> KOT</button>
                              <button className="checkout-btn"
                                disabled={placing||!activeSlot.items.length||hasKitchenQueueForActiveSlot}
                                onClick={() => handlePlace('placed')}
                                title={hasKitchenQueueForActiveSlot?'Kitchen order pending — cannot bill yet':''}>
                                {placing?<><Loader2 size={12} className="mn-spin"/> Placing…</>:<><CheckCircle2 size={12}/> Place Order</>}
                              </button>
                            </div>
                            <button className="print-bill-btn"
                              disabled={placing||!activeSlot.items.length||hasKitchenQueueForActiveSlot}
                              onClick={() => handlePlace('placed_print')}
                              title={hasKitchenQueueForActiveSlot?'Kitchen order pending':''}>
                              <Printer size={11}/> Place & Print Bill
                            </button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </>
          )}

          {/* ── OPEN ORDERS ── */}
          {rightMode === 'open_orders' && (
            <div className="panel-scroll-content">
              <div className="panel-subheader">
                <span>Open Orders</span>
                <button className="panel-refresh-btn" onClick={loadOpenOrders} disabled={ordersLoading}>
                  {ordersLoading?<Loader2 size={12} className="mn-spin"/>:<RefreshCw size={12}/>}
                </button>
              </div>
              {ordersLoading && !openOrders.length
                ? <div style={{ padding:24,textAlign:'center',color:'var(--muted)' }}><Loader2 size={22} className="mn-spin"/></div>
                : openOrders.length === 0
                  ? <div className="panel-empty"><ListOrdered size={30}/><p>No open orders</p></div>
                  : openOrders.map(order => (
                    <OpenOrderCard key={order.id} order={order}
                      completing={completingId === order.id}
                      onModify={() => handleModifyOrder(order)}
                      onVoid={() => handleVoidOrder(order)}
                      onComplete={() => handleCompleteOrder(order)} 

                      onPrint={() => {
  const rawItems = (() => { try { return typeof order.items === 'string' ? JSON.parse(order.items || '[]') : (order.items || []); } catch { return []; } })();
  printTableBill({
    items:         rawItems,
    orderNumber:   order.order_number,
    tableLabel:    order.table_label,
    customerName:  order.customer_name,
    customerPhone: order.customer_phone,
    orderNote:     order.order_note,
    payMethod:     order.payment_method,
    splitCash:     order.split_cash,
    splitCard:     order.split_card,
    splitUpi:      order.split_upi,
    subtotal:      order.subtotal,
    taxAmount:     order.tax_amount,
    grandTotal:    order.grand_total,
    discountAmount:order.discount_amount,
    gstRate:       order.gst_rate,
    gstType:       order.gst_type,
  }, restaurantProfile);
}}


                      onWhatsApp={() => sendBillWhatsApp(order, restaurantName)}
                      onViewBill={() => setShowBillModal(order)}
                    />
                  ))
              }
            </div>
          )}

          {/* ── KITCHEN ── */}
          {rightMode === 'kitchen' && (
            <div className="panel-scroll-content">
              <div className="panel-subheader">
                <span>Kitchen Queue</span>
                <button className="panel-refresh-btn" onClick={loadKitchenTickets} disabled={kitchenLoading}>
                  {kitchenLoading?<Loader2 size={12} className="mn-spin"/>:<RefreshCw size={12}/>}
                </button>
              </div>
              {kitchenLoading && !kitchenTickets.length
                ? <div style={{ padding:24,textAlign:'center',color:'var(--muted)' }}><Loader2 size={22} className="mn-spin"/></div>
                : kitchenTickets.length === 0
                  ? <div className="panel-empty"><ChefHat size={30}/><p>Kitchen is clear</p></div>
                  : kitchenTickets.map(ticket => (
                    <KitchenTicketCard key={ticket.id} ticket={ticket} onDone={() => handleTicketDone(ticket.id)}/>
                  ))
              }
            </div>
          )}
        </aside>
      </main>

      {/* Modals */}
      {showTableModal  && <TablePickerModal tables={pickableTables} openTableIds={openTableIds} onSelect={openTableSlot} onClose={() => setShowTableModal(false)}/>}
      {orderSuccess    && <OrderSuccessModal order={orderSuccess} restaurantName={restaurantName} onPrint={() => printTableBill(orderSuccess, restaurantProfile)} onWhatsApp={() => sendBillWhatsApp(orderSuccess, restaurantName)} onClose={() => setOrderSuccess(null)}/>}
     
     
      {showBillModal   && <BillViewModal order={showBillModal} restaurantProfile={restaurantProfile} 
      onPrint={() => {
  const rawItems = (() => { try { return typeof showBillModal.items === 'string' ? JSON.parse(showBillModal.items || '[]') : (showBillModal.items || []); } catch { return []; } })();
  printTableBill({
    items:         rawItems,
    orderNumber:   showBillModal.order_number,
    tableLabel:    showBillModal.table_label,
    customerName:  showBillModal.customer_name,
    customerPhone: showBillModal.customer_phone,
    orderNote:     showBillModal.order_note,
    payMethod:     showBillModal.payment_method,
    splitCash:     showBillModal.split_cash,
    splitCard:     showBillModal.split_card,
    splitUpi:      showBillModal.split_upi,
    subtotal:      showBillModal.subtotal,
    taxAmount:     showBillModal.tax_amount,
    grandTotal:    showBillModal.grand_total,
    discountAmount:showBillModal.discount_amount,
    gstRate:       showBillModal.gst_rate,
    gstType:       showBillModal.gst_type,
  }, restaurantProfile);
}}
 onWhatsApp={() => sendBillWhatsApp(showBillModal, restaurantName)} onClose={() => setShowBillModal(null)}/>}
      

       
      
      {cancelModalOrder && (
        <div className="modal-overlay" onClick={() => setCancelModalOrder(null)}>
          <div className="cancel-modal" onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14 }}>
              <h3 style={{ fontSize:14,fontWeight:800,color:'var(--ink)',display:'flex',alignItems:'center',gap:6 }}><Ban size={15} style={{ color:'#ef4444' }}/> Cancel Order #{cancelModalOrder.order_number}</h3>
              <button onClick={() => setCancelModalOrder(null)} style={{ border:'none',background:'none',cursor:'pointer',color:'var(--muted)',display:'flex' }}><X size={14}/></button>
            </div>
            <label style={{ fontSize:11,fontWeight:700,color:'var(--muted)',display:'block',marginBottom:6 }}>Cancellation Reason *</label>
            <textarea
              className="cancel-reason-input"
              placeholder="e.g. Customer changed mind, Item out of stock…"
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              rows={3}
              autoFocus/>
            <div style={{ display:'flex',gap:8,marginTop:12 }}>
              <button onClick={() => setCancelModalOrder(null)} style={{ flex:1,padding:'9px',borderRadius:8,border:'1px solid rgba(0,0,0,0.10)',background:'#fff',cursor:'pointer',fontSize:12,fontWeight:700,color:'var(--muted)' }}>Keep Order</button>
              <button onClick={handleCancelOrder} style={{ flex:1,padding:'9px',borderRadius:8,border:'none',background:'#ef4444',color:'#fff',cursor:'pointer',fontSize:12,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',gap:5 }}>
                <Ban size={12}/> Cancel Order
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={`mn-toast ${toast.show ? 'show' : ''}`}>
        <CheckCircle2 size={12} className="mn-toast-icon"/> {toast.msg}
      </div>
    </div>
  );
}

/* ── sub-components ── */
function MenuCard({ product, onAdd }) {
  const imgSrc = product.image_url ? `${imgurl}${product.image_url}` : null;
  const [showV, setShowV] = useState(false);
  const click = () => product.has_half_plate ? setShowV(v => !v) : onAdd(product, 'full');
  return (
    <div className="item-card" onClick={click}>
      <div className="item-img">
        {imgSrc?<img src={imgSrc} alt={product.name} onError={e=>e.target.style.display='none'}/>:<div className="item-img-placeholder">🍽️</div>}
        {product.prep_time&&<span className="item-prep"><Clock size={7}/> {product.prep_time}</span>}
        {product.tags?.includes('Popular')&&<span className="item-hot-badge">🔥</span>}
      </div>
      <div className="item-info">
        <h4>{product.name}</h4>
        <div className="item-price-row">
          <span className="price-full">₹{product.price_full.toFixed(2)}</span>
          {product.has_half_plate&&product.price_half&&<span className="price-half">½ ₹{product.price_half.toFixed(2)}</span>}
        </div>
      </div>
      {showV&&product.has_half_plate?(
        <div className="variant-picker" onClick={e=>e.stopPropagation()}>
          <button className="var-btn full" onClick={()=>{onAdd(product,'full');setShowV(false);}}>Full<br/><strong>₹{product.price_full.toFixed(2)}</strong></button>
          {product.price_half&&<button className="var-btn half" onClick={()=>{onAdd(product,'half');setShowV(false);}}>Half<br/><strong>₹{product.price_half.toFixed(2)}</strong></button>}
        </div>
      ):(
        <button className="add-btn" onClick={e=>{e.stopPropagation();click();}}><Plus size={13}/></button>
      )}
    </div>
  );
}

function CartRow({ item, isNew, addedQty, kotSent, onInc, onDec, onRemove, onNote }) {
  const [showNote, setShowNote] = useState(false);
  return (
    <div className={`cart-item ${isNew&&kotSent?'cart-item-new':''}`}>
      <div className="item-details">
        <span className="item-n">{item.name}{item.variant==='half'&&<span className="half-tag"> ½</span>}{isNew&&kotSent&&<span className="new-tag"> NEW</span>}{addedQty&&!isNew&&<span className="new-tag"> +{addedQty}</span>}</span>
        <span className="item-p">₹{(item.price*item.qty).toFixed(2)}</span>
      </div>
      <div className="item-controls">
        <button onClick={onDec}><Minus size={10}/></button>
        <span className="qty-count">{item.qty}</span>
        <button onClick={onInc}><Plus size={10}/></button>
        <button className="item-note-btn" onClick={()=>setShowNote(v=>!v)}><MessageSquare size={9} style={{ color:item.note?'var(--tomato)':'var(--muted)' }}/></button>
        <button className="item-remove-btn" onClick={onRemove}><X size={9}/></button>
      </div>
      {showNote&&<input className="item-note-input" autoFocus placeholder="e.g. no onion…" value={item.note} onChange={e=>onNote(e.target.value)}/>}
    </div>
  );
}

function OpenOrderCard({ order, completing, onModify, onVoid, onComplete, onPrint, onWhatsApp, onViewBill }) {
  const sm       = ORDER_STATUS_META[order.status] || ORDER_STATUS_META.placed;
  const items    = useMemo(()=>{ try { return typeof order.items==='string'?JSON.parse(order.items):(order.items||[]); } catch { return []; } },[order.items]);
  const typeInfo = ORDER_TYPES.find(t=>t.id===order.order_type)||ORDER_TYPES[0];
  const elapsed  = Math.floor((Date.now()-new Date(order.created_at))/60000);
  const canBill  = ['placed','ready','served','placed_print'].includes(order.status);

  return (
    <div className="open-order-card">
      <div className="ooc-header">
        <div style={{ display:'flex',alignItems:'center',gap:5,flexWrap:'wrap' }}>
          <span style={{ fontSize:11,fontWeight:800,color:'var(--ink)' }}>#{order.order_number}</span>
          <span style={{ fontSize:9,fontWeight:700,background:sm.bg,color:sm.color,padding:'2px 7px',borderRadius:100 }}>{sm.label}</span>
          <span style={{ fontSize:9,color:typeInfo.color,fontWeight:700 }}>{typeInfo.label}</span>
          {order.table_label&&<span style={{ fontSize:9,color:'var(--muted)',fontWeight:600 }}>· T-{order.table_label}</span>}
          {order.customer_name&&<span style={{ fontSize:9,color:'var(--muted)' }}>· {order.customer_name}</span>}
        </div>
        <div style={{ display:'flex',alignItems:'center',gap:4 }}>
          <span style={{ fontSize:9,color:elapsed>20?'#ef4444':'var(--muted)',fontWeight:600,display:'flex',alignItems:'center',gap:2 }}><Timer size={9}/> {elapsed}m</span>
          <span style={{ fontSize:11,fontWeight:800,color:'var(--tomato)' }}>₹{parseFloat(order.grand_total||0).toFixed(0)}</span>
        </div>
      </div>
      <div className="ooc-items">
        {items.slice(0,4).map((it,i)=><span key={i} className="ooc-item-chip">{it.name}{it.variant==='half'?' ½':''} ×{it.qty}</span>)}
        {items.length>4&&<span className="ooc-item-chip">+{items.length-4} more</span>}
      </div>

      {/* Primary actions */}
      <div className="ooc-actions">
        <button className="ooc-btn modify" onClick={onModify}><Edit3 size={11}/> Modify</button>
        <button className="ooc-btn complete" onClick={onComplete} disabled={completing}>
          {completing?<Loader2 size={11} className="mn-spin"/>:<CheckCheck size={11}/>} Complete
        </button>
        <button className="ooc-btn void" onClick={onVoid}><Ban size={11}/> Cancel</button>
      </div>

      {/* Bill actions */}
      <div className="ooc-bill-actions">
        <button className="ooc-bill-btn" onClick={onViewBill}><FileText size={10}/> View Bill</button>
        <button className="ooc-bill-btn" onClick={onPrint}><Printer size={10}/> Print</button>
        <button className="ooc-bill-btn whatsapp" onClick={onWhatsApp} title={order.customer_phone?`Send to ${order.customer_phone}`:'No phone number'}><Send size={10}/> WhatsApp</button>
      </div>
    </div>
  );
}

function KitchenTicketCard({ ticket, onDone }) {
  const items   = useMemo(()=>{ try { return typeof ticket.items==='string'?JSON.parse(ticket.items):(ticket.items||[]); } catch { return []; } },[ticket.items]);
  const elapsed  = Math.floor((Date.now()-new Date(ticket.created_at))/60000);
  const isUrgent = elapsed>15;
  return (
    <div className={`kitchen-card ${isUrgent?'urgent':''}`}>
      <div className="kc-header">
        <div style={{ display:'flex',alignItems:'center',gap:5 }}>
          <ChefHat size={12} style={{ color:isUrgent?'#ef4444':'#f59e0b' }}/>
          <span style={{ fontSize:11,fontWeight:800,color:'var(--ink)' }}>{ticket.table_label?`Table ${ticket.table_label}`:ticket.order_type||'Order'}</span>
          {ticket.status==='acknowledged'&&<span style={{ fontSize:9,background:'rgba(245,158,11,0.12)',color:'#f59e0b',padding:'1px 6px',borderRadius:100,fontWeight:700 }}>In Progress</span>}
        </div>
        <span style={{ fontSize:9,fontWeight:700,color:isUrgent?'#ef4444':'var(--muted)',display:'flex',alignItems:'center',gap:2 }}><Timer size={9}/> {elapsed}m {isUrgent&&'⚠️'}</span>
      </div>
      <div className="kc-items">
        {items.map((it,i)=>(
          <div key={i} className="kc-item">
            <span className="kc-qty">{it.qty}×</span>
            <span className="kc-name">{it.name}{it.variant==='half'?' (½)':''}</span>
            {it.note&&<span className="kc-note">· {it.note}</span>}
          </div>
        ))}
      </div>
      <button className="kc-done-btn" onClick={onDone}><CheckSquare size={12}/> Mark Done</button>
    </div>
  );
}

function TablePickerModal({ tables, openTableIds, onSelect, onClose }) {
  const [search, setSearch] = useState('');
  const grouped = useMemo(()=>{ const f=tables.filter(t=>t.label.toLowerCase().includes(search.toLowerCase())); return f.reduce((acc,t)=>{ const z=t.zone_name||'Floor'; if(!acc[z])acc[z]=[]; acc[z].push(t); return acc; },{}); },[tables,search]);
  const sc = s=>({ available:'#22c55e',occupied:'#ef4444',reserved:'#f59e0b' }[s]||'#9ca3af');
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="table-modal" onClick={e=>e.stopPropagation()}>
        <div className="table-modal-header"><h3><MapPin size={14}/> Select Table</h3><button onClick={onClose}><X size={13}/></button></div>
        <div className="table-modal-search"><Search size={12}/><input placeholder="Search…" value={search} onChange={e=>setSearch(e.target.value)} autoFocus/></div>
        <div className="table-modal-body">
          {Object.keys(grouped).length===0?<div style={{ textAlign:'center',padding:20,color:'var(--muted)' }}>No tables</div>
            :Object.entries(grouped).map(([zone,zT])=>(
              <div key={zone} className="table-zone-group">
                <div className="table-zone-label">{zone}</div>
                <div className="table-zone-grid">
                  {zT.map(t=>{ const open=openTableIds.includes(String(t.id)); return (
                    <button key={t.id} className={`table-pick-cell ${open?'already-open':''}`} style={{ '--sc':sc(t.status) }} onClick={()=>onSelect(t)}>
                      <span className="tpc-label">{t.label}</span>
                      <span className="tpc-cap">{t.capacity}👤</span>
                      <span className="tpc-status">{open?'📝 Open':t.status}</span>
                    </button>
                  ); })}
                </div>
              </div>
            ))
          }
        </div>
        <div className="table-modal-legend">
          {[['#22c55e','Available'],['#ef4444','Occupied'],['#f59e0b','Reserved']].map(([c,l])=>(
            <span key={l} style={{ display:'flex',alignItems:'center',gap:3,fontSize:10,color:'var(--muted)' }}><span style={{ width:6,height:6,borderRadius:'50%',background:c,display:'inline-block' }}/>{l}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function OrderSuccessModal({ order, restaurantName, onPrint, onWhatsApp, onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="success-modal" onClick={e=>e.stopPropagation()}>
        <div className="success-icon">✅</div>
        <h3>Order Placed!</h3>
        <div className="success-num">#{order.order_number||order.id}</div>
        <p style={{ color:'var(--muted)',fontSize:12,marginTop:4 }}>
          {order.order_type==='table'&&order.table_label?`Table ${order.table_label}`:order.order_type}{' · '}₹{parseFloat(order.grand_total||0).toFixed(2)}
        </p>
        <div style={{ display:'flex',gap:8,marginTop:14 }}>
          <button onClick={onPrint} style={{ flex:1,padding:'8px',borderRadius:8,border:'1px solid rgba(0,0,0,0.10)',background:'#fff',cursor:'pointer',fontSize:11,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',gap:4,color:'var(--ink)' }}>
            <Printer size={12}/> Print
          </button>
          <button onClick={onWhatsApp} style={{ flex:1,padding:'8px',borderRadius:8,border:'none',background:'#25D366',color:'#fff',cursor:'pointer',fontSize:11,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',gap:4 }}>
            <Send size={12}/> WhatsApp
          </button>
        </div>
        <button className="success-close-btn" onClick={onClose} style={{ marginTop:8,width:'100%' }}>Continue</button>
      </div>
    </div>
  );
}

// function BillViewModal({ order, restaurantName, onPrint, onWhatsApp, onClose }) {


  function BillViewModal({ order, restaurantProfile = {}, restaurantName, onPrint, onWhatsApp, onClose }) {
  const rName   = restaurantProfile.name    || restaurantName || 'Restaurant';
  const tagline = restaurantProfile.tagline || '';
  const address = [restaurantProfile.address, restaurantProfile.city, restaurantProfile.state, restaurantProfile.pincode].filter(Boolean).join(', ');
  const gstin   = restaurantProfile.gst_number || '';
  const footer  = restaurantProfile.footer_note || 'Thank you! · Powered by Restrowave';


  const items = useMemo(()=>{ try { return typeof order.items==='string'?JSON.parse(order.items):(order.items||[]); } catch { return []; } },[order.items]);
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="bill-view-modal" onClick={e=>e.stopPropagation()}>
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12 }}>
          <h3 style={{ fontSize:14,fontWeight:800,color:'var(--ink)' }}>Bill #{order.order_number}</h3>
          <button onClick={onClose} style={{ border:'none',background:'rgba(0,0,0,0.06)',borderRadius:6,padding:4,cursor:'pointer',display:'flex' }}><X size={13}/></button>
        </div>
        <div className="bill-receipt">
          <div style={{ textAlign:'center',marginBottom:10 }}>
            {/* <div style={{ fontSize:16,fontWeight:800 }}>{restaurantName}</div> */}
            {/* <div style={{ fontSize:10,color:'var(--muted)',marginTop:2 }}>TAX INVOICE</div> */}


            {restaurantProfile.logo_url && <img src={restaurantProfile.logo_url} style={{ maxHeight:44, maxWidth:60, display:'block', margin:'0 auto 6px', objectFit:'contain' }} alt=""/>}
<div style={{ fontSize:16,fontWeight:800 }}>{rName}</div>
{tagline && <div style={{ fontSize:11, color:'var(--muted)', marginTop:1 }}>{tagline}</div>}
{address && <div style={{ fontSize:10, color:'var(--muted)', marginTop:1 }}>{address}</div>}
{restaurantProfile.phone && <div style={{ fontSize:10, color:'var(--muted)' }}>📞 {restaurantProfile.phone}</div>}
{gstin && <div style={{ fontSize:9, color:'var(--muted)', fontWeight:700 }}>GSTIN: {gstin}</div>}
<div style={{ fontSize:10, color:'var(--muted)', marginTop:2 }}>TAX INVOICE</div>



            <div style={{ fontSize:11,marginTop:4 }}>
              {order.order_type==='table'&&order.table_label?`Table ${order.table_label}`:<span style={{ background:'#111',color:'#fff',padding:'1px 8px',borderRadius:3,fontSize:10 }}>{order.order_type?.toUpperCase()}</span>}
            </div>
            {order.customer_name&&<div style={{ fontSize:11,marginTop:3 }}>{order.customer_name}{order.customer_phone&&` · ${order.customer_phone}`}</div>}
            <div style={{ fontSize:10,color:'var(--muted)',marginTop:2 }}>{new Date(order.created_at||Date.now()).toLocaleString('en-IN')}</div>
          </div>
          <div style={{ borderTop:'1px dashed #ccc',borderBottom:'1px dashed #ccc',padding:'6px 0',margin:'6px 0' }}>
            {items.map((it,i)=>(
              <div key={i}>
                <div style={{ display:'flex',justifyContent:'space-between',fontSize:11,fontWeight:600 }}>
                  <span>{it.name}{it.variant==='half'?' (½)':''} ×{it.qty}</span>
                  <span>₹{(parseFloat(it.price)*parseInt(it.qty)).toFixed(2)}</span>
                </div>
                {it.note&&<div style={{ fontSize:9,color:'var(--muted)',paddingLeft:8 }}>↳ {it.note}</div>}
              </div>
            ))}
          </div>
          <div style={{ display:'flex',justifyContent:'space-between',fontSize:11,margin:'3px 0' }}><span>Subtotal</span><span>₹{parseFloat(order.subtotal||0).toFixed(2)}</span></div>
          {parseFloat(order.discount_amount||0)>0&&<div style={{ display:'flex',justifyContent:'space-between',fontSize:11,color:'#16a34a',margin:'3px 0' }}><span>Discount</span><span>−₹{parseFloat(order.discount_amount).toFixed(2)}</span></div>}
          <div style={{ display:'flex',justifyContent:'space-between',fontSize:11,margin:'3px 0' }}><span>GST ({order.gst_rate||5}%)</span><span>₹{parseFloat(order.tax_amount||0).toFixed(2)}</span></div>
          {parseFloat(order.delivery_charge||0)>0&&<div style={{ display:'flex',justifyContent:'space-between',fontSize:11,margin:'3px 0' }}><span>Delivery</span><span>₹{parseFloat(order.delivery_charge).toFixed(2)}</span></div>}
          <div style={{ display:'flex',justifyContent:'space-between',fontSize:15,fontWeight:900,borderTop:'2px solid #111',paddingTop:5,margin:'5px 0' }}><span>TOTAL</span><span>₹{parseFloat(order.grand_total||0).toFixed(2)}</span></div>
          <div style={{ display:'flex',justifyContent:'space-between',fontSize:10,color:'var(--muted)' }}><span>Payment</span><span>{(order.payment_method||'cash').toUpperCase()}</span></div>
          {order.order_note&&<div style={{ fontSize:10,color:'var(--muted)',marginTop:4,fontStyle:'italic' }}>Note: {order.order_note}</div>}
          {/* <div style={{ textAlign:'center',fontSize:10,color:'var(--muted)',marginTop:8 }}>Thank you! · Powered by Restrowave</div> */}
        
        <div style={{ textAlign:'center',fontSize:10,color:'var(--muted)',marginTop:8 }}>{footer}</div>

        </div>
        <div style={{ display:'flex',gap:8,marginTop:12 }}>
          <button onClick={onPrint} style={{ flex:1,padding:'9px',borderRadius:8,border:'1px solid rgba(0,0,0,0.10)',background:'#fff',cursor:'pointer',fontSize:11,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',gap:4 }}><Printer size={12}/> Print</button>
          <button onClick={onWhatsApp} style={{ flex:1,padding:'9px',borderRadius:8,border:'none',background:'#25D366',color:'#fff',cursor:'pointer',fontSize:11,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',gap:4 }}><Send size={12}/> WhatsApp</button>
        </div>
      </div>
    </div>
  );
}
